import type { RadarFilter } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarFilterTypes';
import type {
  RadarCarrier,
  RadarItinerary,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarItineraryTypes';
import type {
  Carrier,
  CarrierMultipleAggregate,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/carriers';

export type MappedCarriers = {
  carriers: Carrier[];
  multiple: CarrierMultipleAggregate | null;
  currentResultIds: number[];
};

const computeMappedCarriers = (
  itineraries: RadarItinerary[],
  filterStats: RadarFilter | undefined,
): MappedCarriers => {
  const priceById = new Map<number, number>();
  filterStats?.carriers.forEach((carrier) => {
    priceById.set(carrier.id, carrier.rawMinPrice ?? 0);
  });

  const carrierIdsMap = new Map<number, true>();
  const carriers: Carrier[] = [];

  const mapCarrier = (carrier: RadarCarrier): void => {
    if (!carrierIdsMap.get(carrier.id)) {
      carriers.push({
        id: carrier.id,
        name: carrier.name,
        alt_id: carrier.alternateId,
        display_code: carrier.displayCode,
        alliance: carrier.allianceId,
        price: priceById.get(carrier.id) ?? 0,
      });
      carrierIdsMap.set(carrier.id, true);
    }
  };

  itineraries.forEach((itinerary) => {
    itinerary.legs.forEach((leg) => {
      leg.segments.forEach((segment) => {
        mapCarrier(segment.marketingCarrier);
        mapCarrier(segment.operatingCarrier);
      });
    });
  });

  const multiple: CarrierMultipleAggregate | null =
    filterStats?.multipleCarriers?.rawMinPrice == null
      ? null
      : { price: filterStats.multipleCarriers.rawMinPrice };

  // When `filterStats` is absent (in-flight or partial Radar responses) the
  // selector's `currentResultIds ?? Object.keys(byId)` fallback won't fire on
  // an empty array, so derive ids from the itineraries instead — keeps the
  // airlines filter populated rather than silently rendering nothing.
  const currentResultIds =
    filterStats?.carriers.map((carrier) => carrier.id) ?? carriers.map((carrier) => carrier.id);

  return { carriers, multiple, currentResultIds };
};

// Both the entity reducer and the carriers filter reducer call this with the
// same `action.payload.response` references inside one combineReducers pass —
// the second call is a guaranteed cache hit on a busy market.
let lastInputs: {
  itineraries: RadarItinerary[];
  filterStats: RadarFilter | undefined;
} | null = null;
let lastOutput: MappedCarriers | null = null;

export const mapCarriers = (
  itineraries: RadarItinerary[],
  filterStats: RadarFilter | undefined,
): MappedCarriers => {
  if (
    lastOutput &&
    lastInputs?.itineraries === itineraries &&
    lastInputs.filterStats === filterStats
  ) {
    return lastOutput;
  }
  const output = computeMappedCarriers(itineraries, filterStats);
  lastInputs = { itineraries, filterStats };
  lastOutput = output;
  return output;
};
